import { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { HotelRating as HotelRatingType, ReviewSummary } from '../../../../types/HotelFromApi';
import { REVIEW_SUB_CATEGORIES } from '../../../core/constants';
import HotelRating from '../HotelRating/HotelRating';
import SkipTranslationText from '../../common/SkipTranslationText';
import RatingBar from '../RatingBar/RatingBar';
import testIds from '../ReviewAndAccess/testIds';

type Props = {
  hotelRating?: HotelRatingType;
  reviewSummary?: ReviewSummary;
};

const HotelReviewSummary = (props: Props) => {
  const { hotelRating, reviewSummary } = props;

  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  return (
    hotelRating && (
      <div className="flex flex-col items-start gap-2 md:gap-4">
        <HotelRating hotelRating={hotelRating} fontClassName="md:text-2xl md:px-3" />
        {reviewSummary?.positiveSummary && (
          <p
            className={classNames('text-sm text-gray-950', {
              'line-clamp-3 md:line-clamp-none': !showMore,
            })}
            data-testid={testIds.positive_summary}
          >
            {reviewSummary?.positiveSummary}
          </p>
        )}

        <div
          className={classNames(
            'md:flex flex-col gap-2 md:gap-4 w-full',
            showMore ? 'flex' : 'hidden'
          )}
        >
          <div className="flex grid-cols-3 flex-col gap-2 text-center text-xl md:grid">
            {REVIEW_SUB_CATEGORIES.filter((category) => {
              const categoryRating =
                hotelRating?.subRatings?.[category as keyof typeof hotelRating.subRatings];
              // Don't display hotel review category if there is no rating for that category (e.g. 0 or null)
              return !!categoryRating;
            }).map((category, renderIndex) => {
              const categoryRating =
                hotelRating?.subRatings?.[category as keyof typeof hotelRating.subRatings];

              return (
                <div key={category} className="flex items-center gap-2 text-gray-950">
                  <RatingBar index={renderIndex} label={category} value={categoryRating!} max={5} />
                </div>
              );
            })}
          </div>
          <p className="text-sm text-gray-600">
            <SkipTranslationText
              i18nKey="hotel:review.based_on_reviews"
              data-testid={testIds.number_of_reviews}
              values={{ count: hotelRating?.totalNoOfReviews?.toLocaleString() }}
            />
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowMore(!showMore)}
          className="text-sm text-[#008099] md:hidden"
        >
          {showMore ? t('common:button.show_less') : t('common:button.show_more')}
        </button>
      </div>
    )
  );
};

export default HotelReviewSummary;
